import { goToLoginPage } from '@/_utils';
import { refreshTokenService } from './User';
import { TokenType } from '@/constants/types/misc/Auth';
import { timeFetch } from '@/libs/trackSlowFetches';

/** Returns default headers with authorization */
const getHeaders = (setHeaders: boolean) => {
  const defaultHeaders: { [key: string]: string } = {
    Accept: 'application/json',
  };

  if (setHeaders) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  // Set token
  const token = localStorage.getItem('token');
  if (token) {
    defaultHeaders.Authorization = token;
  }
  // Set admin selected schema
  const adminSchema = localStorage.getItem('admin_schema');

  if (adminSchema) {
    defaultHeaders.Schema = adminSchema;
  }

  return defaultHeaders;
};

/** Customized fetch function that handles the authentication system */
const Fetch = async (
  url: string,
  init: RequestInit = {},
  setHeaders: boolean = true,
  preventRefreshToken: boolean = false,
  signals?: (AbortSignal | undefined)[]
): Promise<Response> => {
  // Forward the abort event from any provided signal
  if (signals) {
    // Combine multiple abort signals into one
    const controller = new AbortController();
    const { signal } = controller;
    signals.forEach((abortSignal) => {
      if (abortSignal) {
        abortSignal.addEventListener('abort', () => controller.abort());
      }
    });
    // Attach the combined signal to the request
    init.signal = signal;
  }

  // Set headers
  init.headers = { ...init.headers, ...getHeaders(setHeaders) };

  // Send request
  const fullUrl = `${process.env.NEXT_PUBLIC_API_URL}/${url}`;
  const timed = timeFetch(fullUrl, init.method);
  let response: Response;
  try {
    response = await fetch(fullUrl, init);
  } catch (error) {
    // Aborts are cancellations (e.g. TanStack dropping a stale query), not failures
    if ((error as Error)?.name !== 'AbortError') timed(0);
    throw error;
  }
  timed(response.status);
  if (!response.ok) {
    // Unauthorized
    if (response.status === 401) {
      // Refresh token
      if (!preventRefreshToken) {
        const refreshTokenResponse = await refreshTokenService();
        // Retry request
        if (refreshTokenResponse) {
          return Fetch(url, init, setHeaders, true);
        }
      }
      // Loggout and redirect to unauthorize page
      goToLoginPage({ returnUrl: true });
    }
  }
  return response;
};
export default Fetch;

export const decodeToken = (token: string): TokenType => {
  return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
};

export const getRawToken = () => {
  if (typeof window !== 'undefined') {
    const token = window.localStorage?.getItem('token');
    if (token) {
      return token;
    }
  }
};

export const getToken = () => {
  if (typeof window !== 'undefined') {
    const token = window.localStorage?.getItem('token');
    if (token) {
      return decodeToken(token);
    }
  }
};

/** Returns the query parameters as string from the object's keys and values */
export const getQueryParams = (params: { [key: string]: any }) => {
  let queryParams = '';
  for (const paramName in params) {
    const paramValue = params[paramName];
    if (paramValue !== undefined) {
      queryParams += Array.isArray(paramValue)
        ? `&${paramName}=${paramValue.map((v: any) => encodeURIComponent(v)).join(',')}`
        : `&${paramName}=${encodeURIComponent(paramValue)}`;
    }
  }
  if (queryParams.length) {
    queryParams = `?${queryParams.slice(1)}`;
  }
  return queryParams;
};

export const getQuotationFastoLink = (orderId: number) => {
  const tokenInfo = getToken();

  if (!tokenInfo) return;

  const subsidiaryId = tokenInfo['custom:subsidiary_id'];
  const schemaName = tokenInfo['custom:schema_name'];

  return `${process.env.NEXT_PUBLIC_FASTO_URL}${schemaName}/${subsidiaryId}/quote/${orderId}`;
};
